import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Target, GraduationCap, Users, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BENEFITS = [
  { key: 'technique', Icon: Target, accent: '#A855F7', title: 'Technique-first coaching', desc: 'Efficient movement first, then speed and distance — fewer injuries, stronger foundations.' },
  { key: 'every_level', Icon: Users, accent: '#7E22CE', title: 'Every age and level', desc: 'Kids from 5, teens, beginners, recreational runners and performance athletes.' },
  { key: 'coaches', Icon: GraduationCap, accent: '#4C1D95', title: 'Experienced coaching team', desc: '11 coaches and 5,000+ athletes coached since 2020 across Kuala Lumpur and Shah Alam.' },
];

const resolvePhoto = (url) => (!url ? null : url.startsWith('http') ? url : `${API}${url}`);
const initials = (name) => (name || '').replace(/^Coach\s+/i, '').split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const CoachAvatar = ({ coach }) => {
  const [broken, setBroken] = useState(false);
  const src = resolvePhoto(coach.photo_url);
  const name = (coach.name || coach.full_name || '').replace(/^Coach\s+/i, 'Coach ');
  return (
    <Link to={`/coaches/${coach.slug || coach.id}`} className="flex flex-col items-center text-center w-[112px] shrink-0 group" data-testid={`featured-coach-${coach.slug || coach.id}`}>
      <div className="w-20 h-20 rounded-full overflow-hidden ring-2 ring-white shadow-md bg-gradient-to-br from-[#A855F7] to-[#6D28D9] flex items-center justify-center group-hover:ring-[#A855F7] transition-all">
        {src && !broken ? <img src={src} alt={`${name}, LGRA running coach`} width="80" height="80" loading="lazy" className="w-full h-full object-cover" onError={() => setBroken(true)} /> : <span className="text-white font-black text-lg">{initials(coach.name)}</span>}
      </div>
      <div className="mt-2 text-sm font-black tracking-tight text-[#0F172A] leading-tight group-hover:text-[#7E22CE]">{name}</div>
      <div className="text-[11px] text-slate-500 leading-tight">{coach.position || 'LGRA Coach'}</div>
    </Link>
  );
};

/** Section 6 — Why LGRA & Featured Coaches (merged). */
export const WhyLGRACoachesSection = () => {
  const [coaches, setCoaches] = useState([]);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/public/coaches`).then((r) => {
      if (!alive) return;
      const list = Array.isArray(r.data) ? r.data : (r.data?.coaches || []);
      setCoaches([...list].sort((a, b) => (b.is_head_coach ? 1 : 0) - (a.is_head_coach ? 1 : 0)).slice(0, 4));
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <section id="why" className="bg-white border-y border-[#F3E8FF] py-16 md:py-20 scroll-mt-20" data-testid="why-lgra-section" aria-labelledby="why-heading">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-10 text-center">
          <div className="text-[10px] uppercase tracking-widest text-[#A855F7] font-black mb-2">Why LGRA</div>
          <h2 id="why-heading" className="text-3xl md:text-5xl font-black tracking-tight">Why Runners Train With LGRA</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="why-benefits-grid">
          {BENEFITS.map(({ key, Icon, accent, title, desc }) => (
            <div key={key} className="rounded-2xl bg-gradient-to-b from-[#F5F3FF] to-white border-2 border-[#F3E8FF] hover:border-[#A855F7] transition-all p-6" data-testid={`why-${key}`}>
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 shadow-sm" style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accent}CC 100%)` }}><Icon size={22} className="text-white" strokeWidth={2.25} /></div>
              <h3 className="text-lg font-black tracking-tight mb-1.5">{title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
        {coaches.length > 0 && (
          <div className="mt-10 rounded-3xl border border-[#EDE9FE] bg-gradient-to-b from-white to-[#FAF9FF] p-6 md:p-8 grid md:grid-cols-[1fr_auto] gap-6 items-center" data-testid="featured-coaches">
            <div>
              <h3 className="text-xl md:text-2xl font-black tracking-tight text-[#0F172A]">Meet the coaches</h3>
              <p className="mt-2 text-sm text-slate-600 max-w-md">Head coaches and specialists based in Bukit Jalil and Alam Impian. Every coach has a public profile you can read before you book.</p>
              <div className="mt-4 flex flex-wrap gap-4">
                <Link to="/coaches" className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-[#7C3AED] hover:text-[#4C1D95]" data-testid="why-coaches-link">All coaches <ArrowRight size={12} /></Link>
                <Link to="/runners-stories" className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-slate-600 hover:text-[#7E22CE]" data-testid="why-stories-link">Runners’ stories <ArrowRight size={12} /></Link>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 justify-center md:justify-end">{coaches.map((c) => <CoachAvatar key={c.id || c.slug} coach={c} />)}</div>
          </div>
        )}
      </div>
    </section>
  );
};

export default WhyLGRACoachesSection;
